import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CustomPDFViewer from "./CustomPDFViewer";
import { toast } from "react-toastify";

const downloadButtonShow = false; // Set to false to hide download button

const ViewFileModalInspectionList = ({ openModal, modalData, handleCloseModal, modalType = "documents" }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  // Documents fields
  const documentFields = [
    { label: "Inspection Call/PO", key: "clientRequestFile" },
    { label: "QAP", key: "qapDrawings" },
    { label: "Drawing/Datasheet", key: "technicalDatasheet" },
    { label: "Technical Specification", key: "technicalSpecification" },
  ];

  // Attachments fields
  const attachmentFields = [
    { label: "Witness Report", key: "witnessReportPath" },
    { label: "Type Test Certificates", key: "typeTestCertificatesPath" },
    { label: "Review Report", key: "reviewReportPath" },
    { label: "Calibration Certificate", key: "calibrationCertificatePath" },
    // time sheet key
    { label: "Supporting Documents", key: "supportingDocuments" }, 
  ];

  // Choose which fields to display based on modalType
  const fileFields = modalType === "attachments" ? attachmentFields : documentFields;
  const modalTitle = modalType === "attachments" ? "Attachments" : "Documents";

  const getFileType = (url) => {
    const extension = url.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension))
      return "image";
    if (["pdf"].includes(extension)) return "pdf";
    if (["doc", "docx"].includes(extension)) return "doc";
    return "unknown";
  };

  const getFiles = (value) => {
    if (!value) return [];
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const handleFilePreview = (url) => {
    const type = getFileType(url);
    setPreviewUrl(url);
    setFileType(type);
  };

  const handleClosePreview = () => {
    setPreviewUrl(null);
    setFileType(null);
  };

  const handleDownload = async (fileUrl) => {
    try {
      const response = await fetch(fileUrl, {
        method: "GET",
        mode: "cors",
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Try to extract file name from URL or use fallback
      const fileName = fileUrl.split("/").pop().split("?")[0];
      a.download = fileName || "download.pdf";

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloading ${fileName}...`);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <>
      {/* MAIN MODAL */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            {modalTitle}
          </Typography>

          {modalData && (
            <>
              {fileFields?.filter(({ key }) => modalData[key])?.length > 0 ? (
                fileFields
                  ?.filter(({ key }) => modalData[key])
                  ?.map(({ label, key }, ind) => {
                    const files = getFiles(modalData[key]);
                    return (
                      files.length > 0 && (
                        <Box key={key} mt={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ind + 1}. {label}
                          </Typography>

                          {files.map((fileUrl, fileIndex) => {
                            const fileName = decodeURIComponent(
                              fileUrl.split("__").pop()
                            );

                            return (
                              <Box
                                key={fileIndex}
                                display="flex"
                                alignItems="center"
                                pl={2}
                                mt={0.5}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ flexGrow: 1 }}
                                  noWrap
                                >
                                  • {fileName}
                                </Typography>

                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleFilePreview(fileUrl)}
                                    color="primary"
                                  >
                                    <AttachFileIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownload(fileUrl)}
                                    color="secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            );
                          })}
                        </Box>
                      )
                    );
                  })
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mt={2}
                >
                  {modalType === "attachments" ? "Attachments not uploaded." : "Documents not uploaded."}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Modal>

      {/* PREVIEW MODAL */}
      <Modal open={!!previewUrl} onClose={handleClosePreview}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60vw",
            height: "80vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            overflow: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">File Preview</Typography>
            <Box>
              {downloadButtonShow && previewUrl && (
                <Tooltip title="Download File">
                  <IconButton onClick={handleDownload}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                variant="contained"
                size="small"
                color="error"
                sx={{
                  position: "absolute",
                  right: 25,
                  top: 20,
                  height: "25px",
                  minWidth: "40px",
                }}
                onClick={handleClosePreview}
              >
                X
              </Button>
            </Box>
          </Box>

          <Box mt={2}>
            {fileType === "image" && (
              <img
                src={previewUrl}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            )}

            {fileType === "pdf" && previewUrl && (
              <CustomPDFViewer
                url={previewUrl}
                height="70vh"
                canvasHeight="auto"
              />
            )}

            {fileType === "doc" && (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  previewUrl
                )}&embedded=true`}
                width="100%"
                height="70vh"
                frameBorder="0"
                title="DOC Preview"
              ></iframe>
            )}

            {fileType === "unknown" && (
              <Typography>
                Cannot preview this file type.{" "}
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  Click here to download
                </a>
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ViewFileModalInspectionList;
